// pages/sales/SalesList.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Eye,
    Edit,
    Trash2,
    PlusCircle,
    Download,
    Printer,
    Filter,
    Search,
    ArrowUp,
    ArrowDown,
    ChevronLeft,
    ChevronRight,
    Users,
    File
} from 'react-feather';
import Select from 'react-select';
import { DatePicker } from 'antd';
import dayjs from 'dayjs';
import Swal from 'sweetalert2';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { saleActions } from '../../core/action/saleActions';
import { companyActions } from '../../core/action/companyActions';
import { bankActions } from '../../core/action/bankActions';
import ViewSale from '../../core/modals/sales/viewSale';
import EditSale from '../../core/modals/sales/EditSale';

const { RangePicker } = DatePicker;

const SalesList = () => {
    const navigate = useNavigate();

    const [sales, setSales] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [companySettings, setCompanySettings] = useState({});

    // Filters
    const [filters, setFilters] = useState({
        status: '',
        payment_status: '',
        customer_id: 0,
        date_from: '',
        date_to: ''
    });

    // Pagination
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 1
    });

    // Modal state
    const [modalType, setModalType] = useState(null); // 'view', 'edit'
    const [selectedSale, setSelectedSale] = useState(null);

    // PDF dropdown
    const [openPdfDropdown, setOpenPdfDropdown] = useState(null);

    // Sort
    const [sortConfig, setSortConfig] = useState({
        key: 'sale_date',
        direction: 'desc'
    });

    const isInitialMount = useRef(true);
    const prevPageRef = useRef(1);

    // Load company settings
    const loadCompanySettings = async () => {
        try {
            const result = await companyActions.getSettings();
            if (result.success) {
                setCompanySettings(result.data || {});
            }
        } catch (error) {
            console.error('Error loading company settings:', error);
        }
    };

    useEffect(() => {
        loadCompanySettings();
    }, []);

    // Status options
    const statusOptions = [
        { value: '', label: 'All Status' },
        { value: 'pending', label: 'Pending' },
        { value: 'confirmed', label: 'Confirmed' },
        { value: 'processing', label: 'Processing' },
        { value: 'shipped', label: 'Shipped' },
        { value: 'delivered', label: 'Delivered' },
        { value: 'cancelled', label: 'Cancelled' }
    ];

    // Payment status options
    const paymentStatusOptions = [
        { value: '', label: 'All Payment' },
        { value: 'unpaid', label: 'Unpaid' },
        { value: 'partial', label: 'Partial' },
        { value: 'paid', label: 'Paid' }
    ];

    // Customer options
    const [customers, setCustomers] = useState([]);

    // Load customers
    const loadCustomers = async () => {
        try {
            const result = await saleActions.getCustomers({ limit: 1000 });
            if (result.success) {
                setCustomers([
                    { value: 0, label: 'All Customers' },
                    ...result.data.map(c => ({
                        value: c.id,
                        label: `${c.name} (${c.phone || 'No phone'})`
                    }))
                ]);
            }
        } catch (error) {
            console.error('Error loading customers:', error);
        }
    };

    useEffect(() => {
        loadCustomers();
    }, []);

    // Main fetch function
    const fetchSales = async (page = pagination.page) => {
        try {
            setLoading(true);

            const params = {
                page: page,
                limit: pagination.limit,
                sort_by: sortConfig.key,
                sort_order: sortConfig.direction.toUpperCase(),
                ...(searchTerm && { search: searchTerm }),
                ...(filters.status && { status: filters.status }),
                ...(filters.payment_status && { payment_status: filters.payment_status }),
                ...(filters.customer_id > 0 && { customer_id: filters.customer_id }),
                ...(filters.date_from && { date_from: filters.date_from }),
                ...(filters.date_to && { date_to: filters.date_to })
            };

            console.log('Fetching sales with params:', params);

            const result = await saleActions.getSales(params);

            console.log('API Response:', result);

            if (result.success) {
                setSales(result.data || []);
                setPagination(prev => ({
                    ...prev,
                    page: result.page || page,
                    total: result.total,
                    totalPages: result.totalPages || Math.ceil(result.total / pagination.limit)
                }));
                prevPageRef.current = page;
            } else {
                Swal.fire('Error', result.message || 'Failed to load sales', 'error');
                setSales([]);
            }
        } catch (error) {
            console.error('Error fetching sales:', error);
            Swal.fire('Error', 'Failed to load sales', 'error');
            setSales([]);
        } finally {
            setLoading(false);
        }
    };

    // Initial fetch
    useEffect(() => {
        fetchSales(1);
        isInitialMount.current = false;
    }, []);

    // Fetch when page changes
    useEffect(() => {
        if (isInitialMount.current || prevPageRef.current === pagination.page) return;
        fetchSales(pagination.page);
    }, [pagination.page]);

    // Fetch when filters or sort change
    useEffect(() => {
        if (isInitialMount.current) return;

        const timer = setTimeout(() => {
            setPagination(prev => ({ ...prev, page: 1 }));
            setTimeout(() => fetchSales(1), 0);
        }, 300);

        return () => clearTimeout(timer);
    }, [sortConfig, searchTerm, filters]);

    // Handle sort
    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    // Modal handlers
    const handleView = (sale) => {
        setSelectedSale(sale);
        setModalType('view');
    };

    const handleEdit = (sale) => {
        setSelectedSale(sale);
        setModalType('edit');
    };

    const handleModalClose = () => {
        setModalType(null);
        setSelectedSale(null);
    };

    const handleSuccess = () => {
        fetchSales(pagination.page);
        handleModalClose();
    };

    // Handle delete
    const handleDelete = async (id) => {
        Swal.fire({
            title: 'Delete Sale?',
            text: 'Are you sure you want to delete this sale? This action cannot be undone.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const deleteResult = await saleActions.deleteSale(id);
                    if (deleteResult.success) {
                        Swal.fire('Deleted!', 'Sale has been deleted.', 'success');
                        fetchSales(pagination.page);
                    } else {
                        Swal.fire('Error', deleteResult.message || 'Failed to delete sale', 'error');
                    }
                } catch (error) {
                    console.error('Error deleting sale:', error);
                    Swal.fire('Error', 'Failed to delete sale', 'error');
                }
            }
        });
    };

    // PDF Download Function — GST Tax Invoice format
    const downloadSalePDF = async (sale, copyType = 'original') => {
        try {
            setLoading(true);
            Swal.fire({ title: 'Generating PDF...', text: 'Please wait', allowOutsideClick: false, didOpen: () => Swal.showLoading() });

            const [saleResult, bankResult] = await Promise.all([
                saleActions.getSale(sale.id),
                bankActions.getAccounts({ status: 'active' })
            ]);

            if (!saleResult.success || !saleResult.data) {
                Swal.fire({ icon: 'error', title: 'Error', text: saleResult?.message || 'Failed to load sale details', timer: 2000 });
                return;
            }

            const responseData = saleResult.data;
            // API may return { sale: {...}, items: [...] } or flat sale object — match viewSale.jsx logic
            const fullSale = responseData.sale || responseData;
            const items = responseData.items || fullSale.items || [];
            const bankAccounts = bankResult.success ? (bankResult.data || []) : [];
            const defaultBank = bankAccounts.find(b => b.is_default === 1) || bankAccounts[0] || null;

            // ── Number-to-words (Indian system) ────────────────────────────
            const toWords = (num) => {
                const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
                    'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
                const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
                const convert = (n) => {
                    if (n < 20) return ones[n];
                    if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
                    if (n < 1000) return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' ' + convert(n % 100) : '');
                    if (n < 100000) return convert(Math.floor(n / 1000)) + ' Thousand' + (n % 1000 ? ' ' + convert(n % 1000) : '');
                    if (n < 10000000) return convert(Math.floor(n / 100000)) + ' Lakh' + (n % 100000 ? ' ' + convert(n % 100000) : '');
                    return convert(Math.floor(n / 10000000)) + ' Crore' + (n % 10000000 ? ' ' + convert(n % 10000000) : '');
                };
                if (!num || num === 0) return 'INR Zero Only';
                const rupees = Math.floor(Math.abs(num));
                const paise = Math.round((Math.abs(num) - rupees) * 100);
                let result = 'INR ' + convert(rupees);
                if (paise > 0) result += ' and ' + convert(paise) + ' Paise';
                return result + ' Only';
            };

            // ── Page setup ──────────────────────────────────────────────────
            const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
            const PW = doc.internal.pageSize.width;
            //const PH = doc.internal.pageSize.height;
            const M = 7;
            const CW = PW - M * 2;

            // ── Copy type label ─────────────────────────────────────────────
            const copyTypeMap = { original: 'ORIGINAL FOR RECIPIENT', duplicate: 'DUPLICATE FOR SUPPLIER', extra: 'EXTRA COPY' };
            const copyLabel = copyTypeMap[copyType] || 'ORIGINAL FOR RECIPIENT';

            // ── Company (seller) info ────────────────────────────────────────
            const compName = companySettings?.company_name || 'Your Company';
            const compAddress = companySettings?.company_address || '';
            const compGST = companySettings?.gst_number || '';
            const compState = companySettings?.company_state || '';
            const compStateCode = companySettings?.state_code || '';
            const compEmail = companySettings?.company_email || '';
            const compPhone = companySettings?.company_phone || '';

            // ── Sale fields ──────────────────────────────────────────────────
            const invoiceNo = fullSale.invoice_no || sale.invoice_no || 'N/A';
            const invoiceDate = dayjs(fullSale.sale_date || sale.sale_date).format('DD-MMM-YY');
            const buyerOrderNo = fullSale.po_number || sale.po_number || '';
            const dcNumber = fullSale.dc_number || sale.dc_number || '';
            const billNumber = fullSale.bill_number || sale.bill_number || '';
            const salesReference = fullSale.sales_reference || sale.sales_reference || '';
            const paymentMode = fullSale.payment_mode || sale.payment_mode || '';
            const paymentStatus = fullSale.payment_status || sale.payment_status || '';
            const saleStatus = fullSale.status || sale.status || '';
            const remarks = fullSale.remarks || sale.remarks || '';
            const paymentInfo = [paymentMode, paymentStatus].filter(Boolean).join(' / ');
            const statusInfo = saleStatus ? saleStatus.toUpperCase() : '';

            // ── Customer fields ──────────────────────────────────────────────
            const custName = fullSale.customer_name || sale.customer_name || 'N/A';
            const custAddress = fullSale.customer_address || sale.customer_address || '';
            const custGST = fullSale.customer_gst || sale.customer_gst || '';
            const custState = fullSale.customer_state || sale.customer_state || '';
            const custStateCode = fullSale.customer_state_code || sale.customer_state_code || '';

            // ── Financials ───────────────────────────────────────────────────
            const subtotal = parseFloat(fullSale.subtotal || 0);
            const tax = parseFloat(fullSale.tax || 0);
            const cgstRate = parseFloat(fullSale.cgst_rate || 9);
            const sgstRate = parseFloat(fullSale.sgst_rate || 9);
            const cgst = parseFloat(fullSale.cgst || tax / 2);
            const sgst = parseFloat(fullSale.sgst || tax / 2);
            const roundOff = parseFloat(fullSale.round_off || 0);
            const grandTotal = parseFloat(fullSale.grand_total || sale.grand_total || 0);

            // ── Helpers ──────────────────────────────────────────────────────
            const fmt3 = (n) => parseFloat(n || 0).toFixed(3);
            const fmt2 = (n) => parseFloat(n || 0).toFixed(2);
            const BK = [0, 0, 0];
            const setBlack = () => { doc.setTextColor(...BK); doc.setDrawColor(...BK); };
            const hLine = (x1, y, x2) => { doc.setLineWidth(0.3); doc.setDrawColor(...BK); doc.line(x1, y, x2, y); };
            const vLine = (x, y1, y2) => { doc.setLineWidth(0.3); doc.setDrawColor(...BK); doc.line(x, y1, x, y2); };
            const drawBox = (x, y, w, h) => { doc.setLineWidth(0.3); doc.setDrawColor(...BK); doc.rect(x, y, w, h, 'S'); };

            let y = M;

            // ── PAGE TITLE ───────────────────────────────────────────────────
            doc.setFontSize(13); doc.setFont('helvetica', 'bold'); setBlack();
            doc.text('Tax Invoice', PW / 2, y + 6, { align: 'center' });
            doc.setFontSize(8); doc.setFont('helvetica', 'normal');
            doc.text(`(${copyLabel})`, M + CW, y + 6, { align: 'right' });
            y += 10;

            // ── SECTION 1: SELLER INFO (left) + INVOICE GRID (right) ─────────
            const leftW = Math.round(CW * 0.52);
            const rightW = CW - leftW;
            const leftX = M;
            const rightX = M + leftW;
            const numFieldRows = 6;
            const fieldH = 9;
            const headerH = numFieldRows * fieldH;

            // Outer box for both sides
            drawBox(M, y, CW, headerH);
            vLine(rightX, y, y + headerH);

            // ── Company info (left cell) ─────────────────────────────────────
            let cy = y + 4;
            doc.setFontSize(10); doc.setFont('helvetica', 'bold'); setBlack();
            doc.text(compName, leftX + 2, cy); cy += 5;
            doc.setFontSize(7.5); doc.setFont('helvetica', 'normal');
            const addrLines = doc.splitTextToSize(compAddress, leftW - 4);
            doc.text(addrLines, leftX + 2, cy); cy += addrLines.length * 3.8 + 1;
            if (compGST) { doc.text(`GSTIN/UIN: ${compGST}`, leftX + 2, cy); cy += 4; }
            if (compState) { doc.text(`State Name : ${compState}${compStateCode ? ', Code : ' + compStateCode : ''}`, leftX + 2, cy); cy += 4; }
            if (compEmail) { doc.text(`E-Mail : ${compEmail}`, leftX + 2, cy); cy += 4; }
            if (compPhone) { doc.text(`Ph : ${compPhone}`, leftX + 2, cy); }

            // ── Invoice fields grid (right cell) — 6 rows × 2 columns ────────
            const fieldMidX = rightX + rightW / 2;
            const fieldRows = [
                ['Invoice No.', invoiceNo, 'Dated', invoiceDate],
                ['Delivery Note', dcNumber, 'Mode/Terms of Payment', paymentInfo],
                ['Reference No. & Date.', billNumber, 'Other References', salesReference],
                ["Buyer's Order No.", buyerOrderNo, 'Dated', buyerOrderNo ? invoiceDate : ''],
                ['Dispatch Doc No.', dcNumber, 'Delivery Note Date', ''],
                ['Dispatched through', statusInfo, 'Destination', ''],
            ];
            fieldRows.forEach(([lLabel, lVal, rLabel, rVal], i) => {
                const fy = y + i * fieldH;
                if (i > 0) hLine(rightX, fy, rightX + rightW);
                vLine(fieldMidX, fy, fy + fieldH);
                // Left cell
                doc.setFontSize(6.5); doc.setFont('helvetica', 'normal'); doc.setTextColor(80, 80, 80);
                doc.text(lLabel, rightX + 2, fy + 3.5);
                doc.setFontSize(8); doc.setFont('helvetica', 'bold'); setBlack();
                if (lVal) doc.text(lVal, rightX + 2, fy + 7.5);
                // Right cell
                doc.setFontSize(6.5); doc.setFont('helvetica', 'normal'); doc.setTextColor(80, 80, 80);
                doc.text(rLabel, fieldMidX + 2, fy + 3.5);
                doc.setFontSize(8); doc.setFont('helvetica', 'bold'); setBlack();
                if (rVal) doc.text(rVal, fieldMidX + 2, fy + 7.5);
            });
            y += headerH;

            // ── Terms of Delivery / Remarks (full width) ─────────────────────
            const termsH = remarks ? 14 : 7;
            drawBox(M, y, CW, termsH);
            doc.setFontSize(6.5); doc.setFont('helvetica', 'normal'); doc.setTextColor(80, 80, 80);
            doc.text('Terms of Delivery', leftX + 2, y + 4.5);
            if (remarks) {
                doc.setFontSize(6.5); doc.setFont('helvetica', 'normal'); doc.setTextColor(80, 80, 80);
                doc.text('Remarks:', leftX + 2, y + 9);
                doc.setFontSize(8); doc.setFont('helvetica', 'normal'); setBlack();
                doc.text(doc.splitTextToSize(remarks, CW - 30), leftX + 20, y + 9);
            }
            y += termsH;

            // ── SECTION 2: CONSIGNEE + BUYER ────────────────────────────────
            const buildPartyLines = (name, address, gst, state, stateCode) => {
                const lines = [];
                lines.push({ text: name, bold: true, size: 9 });
                if (address) {
                    const wrapped = doc.setFontSize(7.5) && doc.splitTextToSize(address, leftW - 5);
                    (Array.isArray(wrapped) ? wrapped : [wrapped]).forEach(l => lines.push({ text: l, bold: false, size: 7.5 }));
                }
                if (gst) lines.push({ text: `GSTIN/UIN   : ${gst}`, bold: false, size: 7.5 });
                const stLine = `State Name   : ${state}${stateCode ? ', Code : ' + stateCode : ''}`;
                lines.push({ text: stLine, bold: false, size: 7.5 });
                return lines;
            };
            const consigneeLines = buildPartyLines(custName, custAddress, custGST, custState, custStateCode);
            const buyerLines = buildPartyLines(custName, custAddress, custGST, custState, custStateCode);
            const partyLineH = 4.2;
            const partyContentH = Math.max(consigneeLines.length, buyerLines.length) * partyLineH + 10;
            const partyH = Math.max(partyContentH, 35);

            drawBox(M, y, leftW, partyH);
            drawBox(rightX, y, rightW, partyH);

            // Consignee label
            doc.setFontSize(7); doc.setFont('helvetica', 'normal'); doc.setTextColor(80, 80, 80);
            doc.text('Consignee (Ship to)', leftX + 2, y + 4);
            let pyL = y + 9;
            consigneeLines.forEach(l => {
                doc.setFontSize(l.size); doc.setFont('helvetica', l.bold ? 'bold' : 'normal'); setBlack();
                doc.text(l.text, leftX + 2, pyL); pyL += l.bold ? 5 : partyLineH;
            });

            // Buyer label
            doc.setFontSize(7); doc.setFont('helvetica', 'normal'); doc.setTextColor(80, 80, 80);
            doc.text('Buyer (Bill to)', rightX + 2, y + 4);
            let pyR = y + 9;
            buyerLines.forEach(l => {
                doc.setFontSize(l.size); doc.setFont('helvetica', l.bold ? 'bold' : 'normal'); setBlack();
                doc.text(l.text, rightX + 2, pyR); pyR += l.bold ? 5 : partyLineH;
            });
            y += partyH;

            // ── SECTION 3: ITEMS TABLE ───────────────────────────────────────
            const orderedItems = items.length > 0 ? [...items].reverse() : [];
            const tableBody = orderedItems.map((item, idx) => {
                const qty = parseFloat(item.quantity || 1);
                const rate = parseFloat(item.unit_price || 0);
                const amt = parseFloat(item.total_cost || 0);
                const hsnSac = item.hsn_code || item.hsn || '';
                return [
                    (idx + 1).toString(),
                    item.product_name || 'Product',
                    hsnSac,
                    `${qty.toFixed(3)} nos`,
                    rate.toFixed(3),
                    'nos',
                    fmt3(amt)
                ];
            });

            const totalQty = orderedItems.reduce((s, i) => s + parseFloat(i.quantity || 0), 0);

            // Tax & round-off rows (plain rows, no fill)
            tableBody.push(['', 'CGST', '', '', '', '', fmt3(cgst)]);
            tableBody.push(['', 'SGST', '', '', '', '', fmt3(sgst)]);
            if (roundOff !== 0) tableBody.push(['', 'Less :   ROUND OFF', '', '', '', '', `(-)${Math.abs(roundOff).toFixed(3)}`]);

            autoTable(doc, {
                startY: y,
                head: [['Sl\nNo.', 'Description of Goods', 'HSN/SAC', 'Quantity', 'Rate', 'per', 'Amount']],
                body: tableBody,
                foot: [['', 'Total', '', `${totalQty.toFixed(3)} nos`, '', '', `Rs.${fmt2(grandTotal)}`]],
                theme: 'grid',
                headStyles: {
                    fillColor: [255, 255, 255], textColor: [0, 0, 0],
                    fontStyle: 'bold', fontSize: 8, halign: 'center',
                    lineColor: [0, 0, 0], lineWidth: 0.3, cellPadding: 2
                },
                bodyStyles: {
                    fontSize: 8, textColor: [0, 0, 0],
                    lineColor: [0, 0, 0], lineWidth: 0.3, cellPadding: 2
                },
                footStyles: {
                    fillColor: [255, 255, 255], textColor: [0, 0, 0],
                    fontStyle: 'bold', fontSize: 9,
                    lineColor: [0, 0, 0], lineWidth: 0.3, cellPadding: 2
                },
                columnStyles: {
                    0: { cellWidth: 10, halign: 'center' },
                    1: { cellWidth: 'auto', halign: 'left' },
                    2: { cellWidth: 22, halign: 'center' },
                    3: { cellWidth: 26, halign: 'center' },
                    4: { cellWidth: 22, halign: 'right' },
                    5: { cellWidth: 13, halign: 'center' },
                    6: { cellWidth: 28, halign: 'right' },
                },
                margin: { left: M, right: M },
                tableWidth: 'auto',
            });
            y = doc.lastAutoTable.finalY;

            // ── SECTION 4: AMOUNT IN WORDS ───────────────────────────────────
            const amtWordsH = 11;
            drawBox(M, y, CW, amtWordsH);
            doc.setFontSize(7); doc.setFont('helvetica', 'normal'); doc.setTextColor(80, 80, 80);
            doc.text('Amount Chargeable (in words)', leftX + 2, y + 4);
            doc.text('E. & O.E', M + CW - 2, y + 4, { align: 'right' });
            doc.setFontSize(9); doc.setFont('helvetica', 'bold'); setBlack();
            doc.text(toWords(grandTotal), leftX + 2, y + 9);
            y += amtWordsH;

            // ── SECTION 5: HSN/SAC TAX BREAKUP TABLE ─────────────────────────
            // Build per-HSN rows from items
            const hsnMap = {};
            orderedItems.forEach(item => {
                const hsn = item.hsn_code || item.hsn || 'N/A';
                const itemTax = parseFloat(item.tax_amount || 0);
                const taxable = parseFloat(item.total_cost || 0) - itemTax;
                const itemCgst = parseFloat(item.cgst_amount || itemTax / 2);
                const itemSgst = parseFloat(item.sgst_amount || itemTax / 2);
                const rate = parseFloat(item.tax_percent || 18);
                if (!hsnMap[hsn]) hsnMap[hsn] = { taxable: 0, cgstRate: rate / 2, sgstRate: rate / 2, cgst: 0, sgst: 0 };
                hsnMap[hsn].taxable += taxable;
                hsnMap[hsn].cgst += itemCgst;
                hsnMap[hsn].sgst += itemSgst;
            });
            const hsnEntries = Object.entries(hsnMap);
            const hsnRows = hsnEntries.length > 0
                ? hsnEntries.map(([hsn, v]) => [
                    hsn, fmt3(v.taxable),
                    `${v.cgstRate}%`, fmt3(v.cgst),
                    `${v.sgstRate}%`, fmt3(v.sgst),
                    fmt3(v.cgst + v.sgst)
                ])
                : [[orderedItems[0]?.hsn_code || 'N/A', fmt3(subtotal), `${cgstRate}%`, fmt3(cgst), `${sgstRate}%`, fmt3(sgst), fmt3(cgst + sgst)]];

            const hsnTotCgst = hsnEntries.reduce((s, [, v]) => s + v.cgst, 0) || cgst;
            const hsnTotSgst = hsnEntries.reduce((s, [, v]) => s + v.sgst, 0) || sgst;
            const hsnTotTaxable = hsnEntries.reduce((s, [, v]) => s + v.taxable, 0) || subtotal;

            autoTable(doc, {
                startY: y,
                head: [
                    ['HSN/SAC', 'Taxable\nValue',
                        { content: 'CGST', colSpan: 2, styles: { halign: 'center' } },
                        { content: 'SGST/UTGST', colSpan: 2, styles: { halign: 'center' } },
                        'Total\nTax Amount'],
                    ['', '', 'Rate', 'Amount', 'Rate', 'Amount', ''],
                ],
                body: hsnRows,
                foot: [['Total', fmt3(hsnTotTaxable), '', fmt3(hsnTotCgst), '', fmt3(hsnTotSgst), fmt3(hsnTotCgst + hsnTotSgst)]],
                theme: 'grid',
                headStyles: {
                    fillColor: [255, 255, 255], textColor: [0, 0, 0],
                    fontStyle: 'bold', fontSize: 7.5, halign: 'center',
                    lineColor: [0, 0, 0], lineWidth: 0.3, cellPadding: 1.5
                },
                bodyStyles: {
                    fontSize: 8, textColor: [0, 0, 0],
                    lineColor: [0, 0, 0], lineWidth: 0.3, cellPadding: 2, halign: 'center'
                },
                footStyles: {
                    fillColor: [255, 255, 255], textColor: [0, 0, 0],
                    fontStyle: 'bold', fontSize: 8,
                    lineColor: [0, 0, 0], lineWidth: 0.3, cellPadding: 2, halign: 'center'
                },
                margin: { left: M, right: M },
                tableWidth: 'auto',
            });
            y = doc.lastAutoTable.finalY;

            // ── SECTION 6: TAX AMOUNT IN WORDS ───────────────────────────────
            const taxWordsH = 8;
            drawBox(M, y, CW, taxWordsH);
            doc.setFontSize(7); doc.setFont('helvetica', 'normal'); doc.setTextColor(80, 80, 80);
            doc.text('Tax Amount (in words) :', leftX + 2, y + 5);
            doc.setFontSize(8); doc.setFont('helvetica', 'bold'); setBlack();
            doc.text(toWords(hsnTotCgst + hsnTotSgst), leftX + 42, y + 5);
            y += taxWordsH;

            // ── SECTION 7: BOTTOM — DECLARATION | BANK DETAILS | SIGNATURES ──
            const bottomH = 35;
            const bLeftW = Math.round(CW * 0.36);
            const bMidW = Math.round(CW * 0.34);
            //const bRightW = CW - bLeftW - bMidW;
            const bMidX = M + bLeftW;
            const bRightX = bMidX + bMidW;

            drawBox(M, y, CW, bottomH);
            vLine(bMidX, y, y + bottomH);
            vLine(bRightX, y, y + bottomH);

            // Declaration
            doc.setFontSize(7.5); doc.setFont('helvetica', 'bold'); setBlack();
            doc.text('Declaration', leftX + 2, y + 5);
            doc.setFont('helvetica', 'normal'); doc.setFontSize(6.5); doc.setTextColor(50, 50, 50);
            const declLines = doc.splitTextToSize(
                'We declare that this invoice shows the actual price\nof the goods described and that all particulars are\ntrue and correct.',
                bLeftW - 4
            );
            doc.text(declLines, leftX + 2, y + 10);

            // Bank details
            doc.setFontSize(7.5); doc.setFont('helvetica', 'bold'); setBlack();
            doc.text("Company's Bank Details", bMidX + 2, y + 5);
            doc.setFontSize(7.5); doc.setFont('helvetica', 'normal');
            if (defaultBank) {
                const labelX = bMidX + 2;
                const valueX = bMidX + 28;
                let bdy = y + 11;
                const bankRows = [
                    ['Bank Name', defaultBank.bank_name || 'N/A'],
                    ['A/c No.', defaultBank.account_number || 'N/A'],
                    ['Branch & IFS Code', `${defaultBank.branch_name ? defaultBank.branch_name + ' & ' : ''}${defaultBank.ifsc_code || 'N/A'}`],
                ];
                bankRows.forEach(([label, val]) => {
                    doc.setFont('helvetica', 'normal'); doc.setTextColor(60, 60, 60);
                    doc.text(label, labelX, bdy);
                    doc.setFont('helvetica', 'bold'); setBlack();
                    const valLines = doc.splitTextToSize(`: ${val}`, bMidW - 30);
                    doc.text(valLines, valueX, bdy);
                    bdy += valLines.length > 1 ? valLines.length * 3.8 + 1 : 5;
                });
            } else {
                doc.setFont('helvetica', 'normal'); doc.setTextColor(120, 120, 120);
                doc.text('No bank account configured', bMidX + 2, y + 15);
            }

            // Signatures
            doc.setFontSize(7.5); doc.setFont('helvetica', 'normal'); doc.setTextColor(50, 50, 50);
            doc.text("Customer's Seal and Signature", bRightX + 2, y + 5);
            doc.setFontSize(8); doc.setFont('helvetica', 'bold'); setBlack();
            doc.text(`for ${compName}`, bRightX + 2, y + bottomH - 9);
            doc.setFontSize(7); doc.setFont('helvetica', 'normal'); doc.setTextColor(50, 50, 50);
            doc.text('Authorised Signatory', bRightX + 2, y + bottomH - 4);
            y += bottomH;

            // ── SECTION 8: FOOTER ────────────────────────────────────────────
            const footerH = 7;
            drawBox(M, y, CW, footerH);
            doc.setFontSize(7.5); doc.setFont('helvetica', 'italic'); doc.setTextColor(50, 50, 50);
            doc.text('This is a Computer Generated Invoice', PW / 2, y + 4.5, { align: 'center' });
            y += footerH;

            // ── OUTER BORDER ─────────────────────────────────────────────────
            doc.setDrawColor(...BK);
            doc.setLineWidth(0.6);
            doc.rect(M, M + 10, CW, y - M - 10, 'S');

            // ── SAVE ─────────────────────────────────────────────────────────
            Swal.close();
            const copyLabelForFile = { original: 'Original', duplicate: 'Duplicate', extra: 'ExtraCopy' }[copyType] || 'Original';
            const fileName = `Invoice_${invoiceNo.replace(/[^a-zA-Z0-9]/g, '_')}_${copyLabelForFile}.pdf`;
            doc.save(fileName);

            Swal.fire({ icon: 'success', title: 'Success', text: 'Invoice PDF downloaded successfully', timer: 1500, showConfirmButton: false });
        } catch (error) {
            console.error('Error generating PDF:', error);
            Swal.close();
            Swal.fire({ icon: 'error', title: 'PDF Generation Failed', text: error.message || 'Unknown error occurred', confirmButtonText: 'OK' });
        } finally {
            setLoading(false);
        }
    };

    // Handle page change
    const handlePageChange = (pageNumber) => {
        if (pageNumber !== pagination.page) {
            setPagination(prev => ({ ...prev, page: pageNumber }));
        }
    };

    const handleNextPage = () => {
        if (pagination.page < pagination.totalPages) {
            setPagination(prev => ({ ...prev, page: prev.page + 1 }));
        }
    };

    const handlePrevPage = () => {
        if (pagination.page > 1) {
            setPagination(prev => ({ ...prev, page: prev.page - 1 }));
        }
    };

    // Handle search
    const handleSearchInput = (e) => {
        setSearchTerm(e.target.value);
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            setPagination(prev => ({ ...prev, page: 1 }));
        }
    };

    // Reset filters
    const resetFilters = () => {
        setSearchTerm('');
        setFilters({
            status: '',
            payment_status: '',
            customer_id: 0,
            date_from: '',
            date_to: ''
        });
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    // Handle date range change
    const handleDateRangeChange = (dates) => {
        if (dates && dates.length === 2) {
            setFilters(prev => ({
                ...prev,
                date_from: dates[0].format('YYYY-MM-DD'),
                date_to: dates[1].format('YYYY-MM-DD')
            }));
        } else {
            setFilters(prev => ({
                ...prev,
                date_from: '',
                date_to: ''
            }));
        }
    };

    // Handle filter change
    const handleFilterChange = (filterName, value) => {
        setFilters(prev => ({ ...prev, [filterName]: value }));
    };

    // Format functions
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 2
        }).format(amount || 0);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return dayjs(dateString).format('DD/MM/YYYY');
    };

    const getStatusBadge = (status) => {
        const badges = {
            'pending': 'badge bg-warning',
            'confirmed': 'badge bg-info',
            'processing': 'badge bg-primary',
            'shipped': 'badge bg-secondary',
            'delivered': 'badge bg-success',
            'cancelled': 'badge bg-danger'
        };
        return badges[status?.toLowerCase()] || 'badge bg-secondary';
    };

    const getPaymentStatusBadge = (status) => {
        const badges = {
            'unpaid': 'badge bg-danger',
            'partial': 'badge bg-warning',
            'paid': 'badge bg-success'
        };
        return badges[status?.toLowerCase()] || 'badge bg-secondary';
    };

    // Calculate statistics
    const calculateStats = () => {
        const totalSales = pagination.total;
        const totalAmount = sales.reduce((sum, s) => sum + parseFloat(s.grand_total || 0), 0);
        const paidAmount = sales.reduce((sum, s) => sum + parseFloat(s.paid_amount || 0), 0);
        const dueAmount = sales.reduce((sum, s) => sum + parseFloat(s.due_amount || 0), 0);
        const pendingSales = sales.filter(s => s.status === 'pending').length;
        const deliveredSales = sales.filter(s => s.status === 'delivered').length;

        return {
            totalSales,
            totalAmount: totalAmount.toFixed(2),
            paidAmount: paidAmount.toFixed(2),
            dueAmount: dueAmount.toFixed(2),
            pendingSales,
            deliveredSales
        };
    };

    const stats = calculateStats();

    // Calculate current page items indices
    const indexOfFirstItem = sales.length > 0 ? (pagination.page - 1) * pagination.limit + 1 : 0;
    const indexOfLastItem = sales.length > 0 ? Math.min(pagination.page * pagination.limit, pagination.total) : 0;

    return (
        <div className="page-wrapper">
            <div className="content">
                {/* Header */}
                <div className="page-header">
                    <div className="page-title">
                        <h4>Sales Management</h4>
                        <h6>Manage your sales invoices and track payments</h6>
                    </div>
                    <ul className="table-top-head">
                        <li>
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={() => navigate('/add-sales')}
                            >
                                <PlusCircle className="me-2" size={18} />
                                New Sale
                            </button>
                        </li>
                        <li>
                            <button className="btn btn-outline-primary">
                                <Download className="me-2" size={18} />
                                Export
                            </button>
                        </li>
                        <li>
                            <button className="btn btn-outline-secondary">
                                <Printer className="me-2" size={18} />
                                Print
                            </button>
                        </li>
                    </ul>
                </div>

                {/* Statistics Cards */}
                <div className="row mb-4">
                    <div className="col-xl-2 col-sm-4 col-6">
                        <div className="card">
                            <div className="card-body">
                                <h5 className="card-title">Total Sales</h5>
                                <h3 className="card-text text-primary">{stats.totalSales}</h3>
                                <p className="card-text text-muted">All time</p>
                            </div>
                        </div>
                    </div>
                    <div className="col-xl-2 col-sm-4 col-6">
                        <div className="card">
                            <div className="card-body">
                                <h5 className="card-title">Sale Amount</h5>
                                <h3 className="card-text text-success">{formatCurrency(stats.totalAmount)}</h3>
                                <p className="card-text text-muted">Grand total</p>
                            </div>
                        </div>
                    </div>
                    <div className="col-xl-2 col-sm-4 col-6">
                        <div className="card">
                            <div className="card-body">
                                <h5 className="card-title">Paid Amount</h5>
                                <h3 className="card-text text-info">{formatCurrency(stats.paidAmount)}</h3>
                                <p className="card-text text-muted">Amount received</p>
                            </div>
                        </div>
                    </div>
                    <div className="col-xl-2 col-sm-4 col-6">
                        <div className="card">
                            <div className="card-body">
                                <h5 className="card-title">Due Amount</h5>
                                <h3 className="card-text text-danger">{formatCurrency(stats.dueAmount)}</h3>
                                <p className="card-text text-muted">Pending payment</p>
                            </div>
                        </div>
                    </div>
                    <div className="col-xl-2 col-sm-4 col-6">
                        <div className="card">
                            <div className="card-body">
                                <h5 className="card-title">Pending</h5>
                                <h3 className="card-text text-warning">{stats.pendingSales}</h3>
                                <p className="card-text text-muted">Pending sales</p>
                            </div>
                        </div>
                    </div>
                    <div className="col-xl-2 col-sm-4 col-6">
                        <div className="card">
                            <div className="card-body">
                                <h5 className="card-title">Delivered</h5>
                                <h3 className="card-text text-success">{stats.deliveredSales}</h3>
                                <p className="card-text text-muted">Delivered sales</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Search and Filters */}
                <div className="card mb-4">
                    <div className="card-body">
                        <div className="row">
                            <div className="col-lg-3 col-md-6 mb-3">
                                <div className="input-group">
                                    <span className="input-group-text">
                                        <Search size={18} />
                                    </span>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Search by invoice, customer..."
                                        value={searchTerm}
                                        onChange={handleSearchInput}
                                        onKeyPress={handleKeyPress}
                                    />
                                </div>
                            </div>
                            <div className="col-lg-2 col-md-3 col-sm-6 mb-3">
                                <Select
                                    options={statusOptions}
                                    value={statusOptions.find(opt => opt.value === filters.status)}
                                    onChange={(opt) => handleFilterChange('status', opt?.value || '')}
                                    placeholder="All Status"
                                    isClearable
                                />
                            </div>
                            <div className="col-lg-2 col-md-3 col-sm-6 mb-3">
                                <Select
                                    options={paymentStatusOptions}
                                    value={paymentStatusOptions.find(opt => opt.value === filters.payment_status)}
                                    onChange={(opt) => handleFilterChange('payment_status', opt?.value || '')}
                                    placeholder="All Payment"
                                    isClearable
                                />
                            </div>
                            <div className="col-lg-2 col-md-3 col-sm-6 mb-3">
                                <Select
                                    options={customers}
                                    value={customers.find(c => c.value === filters.customer_id)}
                                    onChange={(opt) => handleFilterChange('customer_id', opt?.value || 0)}
                                    placeholder="All Customers"
                                    isClearable
                                />
                            </div>
                            <div className="col-lg-3 col-md-6 mb-3">
                                <RangePicker
                                    className="form-control"
                                    format="DD/MM/YYYY"
                                    onChange={handleDateRangeChange}
                                    placeholder={['From Date', 'To Date']}
                                    style={{ width: '100%' }}
                                />
                            </div>
                            <div className="col-lg-12 text-end">
                                <button
                                    className="btn btn-outline-secondary"
                                    onClick={resetFilters}
                                >
                                    <Filter className="me-2" size={18} />
                                    Reset Filters
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Sales Table */}
                <div className="card">
                    <div className="card-body">
                        {loading ? (
                            <div className="text-center p-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <p className="mt-3">Loading sales...</p>
                            </div>
                        ) : (
                            <>
                                <div className="table-responsive">
                                    <table className="table table-hover">
                                        <thead>
                                            <tr>
                                                <th onClick={() => handleSort('invoice_no')} style={{ cursor: 'pointer' }}>
                                                    Invoice No
                                                    {sortConfig.key === 'invoice_no' && (
                                                        sortConfig.direction === 'asc' ?
                                                            <ArrowUp className="ms-1" size={14} /> :
                                                            <ArrowDown className="ms-1" size={14} />
                                                    )}
                                                </th>
                                                <th onClick={() => handleSort('sale_date')} style={{ cursor: 'pointer' }}>
                                                    Date
                                                    {sortConfig.key === 'sale_date' && (
                                                        sortConfig.direction === 'asc' ?
                                                            <ArrowUp className="ms-1" size={14} /> :
                                                            <ArrowDown className="ms-1" size={14} />
                                                    )}
                                                </th>
                                                <th onClick={() => handleSort('customer_name')} style={{ cursor: 'pointer' }}>
                                                    Customer
                                                    {sortConfig.key === 'customer_name' && (
                                                        sortConfig.direction === 'asc' ?
                                                            <ArrowUp className="ms-1" size={14} /> :
                                                            <ArrowDown className="ms-1" size={14} />
                                                    )}
                                                </th>
                                                <th onClick={() => handleSort('grand_total')} style={{ cursor: 'pointer' }}>
                                                    Amount
                                                    {sortConfig.key === 'grand_total' && (
                                                        sortConfig.direction === 'asc' ?
                                                            <ArrowUp className="ms-1" size={14} /> :
                                                            <ArrowDown className="ms-1" size={14} />
                                                    )}
                                                </th>
                                                <th>Status</th>
                                                <th>Payment</th>
                                                <th>Payment Info</th>
                                                <th onClick={() => handleSort('due_amount')} style={{ cursor: 'pointer' }}>
                                                    Due Amount
                                                    {sortConfig.key === 'due_amount' && (
                                                        sortConfig.direction === 'asc' ?
                                                            <ArrowUp className="ms-1" size={14} /> :
                                                            <ArrowDown className="ms-1" size={14} />
                                                    )}
                                                </th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {sales.length === 0 ? (
                                                <tr>
                                                    <td colSpan="9" className="text-center py-5">
                                                        <div className="text-muted">
                                                            <Users size={48} className="mb-3" />
                                                            <h5>No sales found</h5>
                                                            <p>Create your first sale to get started</p>
                                                            <button
                                                                className="btn btn-primary"
                                                                onClick={() => navigate('/add-sales')}
                                                            >
                                                                <PlusCircle size={16} className="me-2" />
                                                                New Sale
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ) : (
                                                sales.map((sale) => (
                                                    <tr key={sale.id}>
                                                        <td>
                                                            <strong>{sale.invoice_no}</strong>
                                                            <br />
                                                            <small className="text-muted">
                                                                Created: {formatDate(sale.created_at)}
                                                            </small>
                                                        </td>
                                                        <td>{formatDate(sale.sale_date)}</td>
                                                        <td>
                                                            <div className="d-flex align-items-center">
                                                                <div className="avatar avatar-sm bg-primary rounded-circle d-flex align-items-center justify-content-center text-white me-2">
                                                                    {sale.customer_name?.charAt(0).toUpperCase()}
                                                                </div>
                                                                <div>{sale.customer_name}</div>
                                                            </div>
                                                            <small className="text-muted d-block">
                                                                {sale.customer_phone || ''}
                                                            </small>
                                                        </td>
                                                        <td>
                                                            <strong>{formatCurrency(sale.grand_total)}</strong>
                                                            <small className="d-block text-muted">
                                                                Items: {sale.items_count || 0} | Qty: {sale.total_qty || 0}
                                                            </small>
                                                        </td>
                                                        <td>
                                                            <span className={getStatusBadge(sale.status)}>
                                                                {sale.status?.toUpperCase()}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <span className={getPaymentStatusBadge(sale.payment_status)}>
                                                                {sale.payment_status?.toUpperCase()}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <div className="payment-info">
                                                                {sale.payment_count > 0 ? (
                                                                    <>
                                                                        <span className="badge bg-info me-1" title="Number of payments">
                                                                            {sale.payment_count} payment{sale.payment_count > 1 ? 's' : ''}
                                                                        </span>
                                                                        {sale.last_payment_date && (
                                                                            <small className="d-block text-muted">
                                                                                Last: {formatDate(sale.last_payment_date)}
                                                                                {sale.last_payment_method && (
                                                                                    <> via <span className="text-capitalize">{sale.last_payment_method}</span></>
                                                                                )}
                                                                            </small>
                                                                        )}
                                                                    </>
                                                                ) : (
                                                                    <span className="text-muted">No payments</span>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <span className={sale.due_amount > 0 ? 'text-danger fw-bold' : 'text-success'}>
                                                                {formatCurrency(sale.due_amount)}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <div className="btn-group btn-group-sm" role="group">
                                                                <button
                                                                    className="btn btn-outline-info"
                                                                    onClick={() => handleView(sale)}
                                                                    title="View Details"
                                                                >
                                                                    <Eye size={14} />
                                                                </button>
                                                                <button
                                                                    className="btn btn-outline-primary"
                                                                    onClick={() => handleEdit(sale)}
                                                                    title="Edit"
                                                                >
                                                                    <Edit size={14} />
                                                                </button>
                                                                {/* PDF Download Dropdown */}
                                                                <div className="btn-group">
                                                                    <button
                                                                        className="btn btn-outline-warning dropdown-toggle"
                                                                        title="Download PDF"
                                                                        disabled={loading}
                                                                        onClick={() => setOpenPdfDropdown(openPdfDropdown === sale.id ? null : sale.id)}
                                                                    >
                                                                        <File size={14} />
                                                                    </button>
                                                                    {openPdfDropdown === sale.id && (
                                                                        <ul
                                                                            className="dropdown-menu show"
                                                                            style={{ minWidth: '170px', position: 'absolute', zIndex: 1050, top: '100%', right: 0, left: 'auto' }}
                                                                            onMouseLeave={() => setOpenPdfDropdown(null)}
                                                                        >
                                                                            <li>
                                                                                <button
                                                                                    className="dropdown-item"
                                                                                    onClick={() => { downloadSalePDF(sale, 'original'); setOpenPdfDropdown(null); }}
                                                                                >
                                                                                    <File size={13} className="me-2 text-success" />
                                                                                    Original Receipt
                                                                                </button>
                                                                            </li>
                                                                            <li>
                                                                                <button
                                                                                    className="dropdown-item"
                                                                                    onClick={() => { downloadSalePDF(sale, 'duplicate'); setOpenPdfDropdown(null); }}
                                                                                >
                                                                                    <File size={13} className="me-2 text-warning" />
                                                                                    Duplicate Receipt
                                                                                </button>
                                                                            </li>
                                                                            <li>
                                                                                <button
                                                                                    className="dropdown-item"
                                                                                    onClick={() => { downloadSalePDF(sale, 'extra'); setOpenPdfDropdown(null); }}
                                                                                >
                                                                                    <File size={13} className="me-2 text-primary" />
                                                                                    Extra Copy
                                                                                </button>
                                                                            </li>
                                                                        </ul>
                                                                    )}
                                                                </div>
                                                                <button
                                                                    className="btn btn-outline-danger"
                                                                    onClick={() => handleDelete(sale.id)}
                                                                    title="Delete"
                                                                >
                                                                    <Trash2 size={14} />
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Pagination */}
                                {pagination.total > 0 && (
                                    <div className="d-flex justify-content-between align-items-center mt-3">
                                        <div>
                                            Showing <strong>{indexOfFirstItem}-{indexOfLastItem}</strong> of{' '}
                                            <strong>{pagination.total}</strong> sales
                                            <span className="text-muted ms-2">(Page {pagination.page} of {pagination.totalPages})</span>
                                        </div>
                                        <nav>
                                            <ul className="pagination mb-0">
                                                <li className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}>
                                                    <button
                                                        className="page-link"
                                                        onClick={handlePrevPage}
                                                        disabled={pagination.page === 1}
                                                    >
                                                        <ChevronLeft size={16} />
                                                    </button>
                                                </li>

                                                {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                                                    let pageNumber;
                                                    if (pagination.totalPages <= 5) {
                                                        pageNumber = i + 1;
                                                    } else if (pagination.page <= 3) {
                                                        pageNumber = i + 1;
                                                    } else if (pagination.page >= pagination.totalPages - 2) {
                                                        pageNumber = pagination.totalPages - 4 + i;
                                                    } else {
                                                        pageNumber = pagination.page - 2 + i;
                                                    }

                                                    return (
                                                        <li
                                                            key={pageNumber}
                                                            className={`page-item ${pagination.page === pageNumber ? 'active' : ''}`}
                                                        >
                                                            <button
                                                                className="page-link"
                                                                onClick={() => handlePageChange(pageNumber)}
                                                            >
                                                                {pageNumber}
                                                            </button>
                                                        </li>
                                                    );
                                                })}

                                                <li className={`page-item ${pagination.page === pagination.totalPages ? 'disabled' : ''}`}>
                                                    <button
                                                        className="page-link"
                                                        onClick={handleNextPage}
                                                        disabled={pagination.page === pagination.totalPages}
                                                    >
                                                        <ChevronRight size={16} />
                                                    </button>
                                                </li>
                                            </ul>
                                        </nav>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>

                {/* Modals */}
                {modalType === 'view' && selectedSale && (
                    <ViewSale
                        sale={selectedSale}
                        onClose={handleModalClose}
                    />
                )}

                {modalType === 'edit' && selectedSale && (
                    <EditSale
                        saleId={selectedSale.id}
                        onSuccess={handleSuccess}
                        onClose={handleModalClose}
                        isOpen={true}
                    />
                )}
            </div>
        </div>
    );
};

export default SalesList;
